import TermsOfServiceClient from '@/api/TermsOfServiceClient';
import dayjs from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';

dayjs.extend(dayjsUtcPlugin);

const termsOfService = {
  state: {
    acceptanceRequired: false,
    currentTosCheckInProgress: false,
    errors: [],
    eulaId: null,
    lastCheckedTosDate: null,
    termsOfServiceAccepted: false,
    tosContentUrl: '',
    tosPrintContent: null,
    tosSuccessToastShown: false,
  },
  mutations: {
    setTosAcceptanceRequired(state, newVal) {
      state.acceptanceRequired = newVal;
    },
    setTosContentUrl(state, newVal) {
      state.tosContentUrl = newVal;
    },
    setTermsOfServiceAccepted(state, newVal) {
      state.termsOfServiceAccepted = newVal;
    },
    setEulaId(state, newId) {
      state.eulaId = newId;
    },
    setLastCheckedTosDate(state, timestamp) {
      state.lastCheckedTosDate = timestamp;
    },
    setTosErrors(state, errors) {
      state.errors = errors;
    },
    setTosPrintContent(state, newVal) {
      state.tosPrintContent = newVal;
    },
    setCurrentTosCheckInProgress(state, newVal) {
      state.currentTosCheckInProgress = newVal;
    },
    setTosSuccessToastShown(state, newVal) {
      state.tosSuccessToastShown = newVal;
    },
  },
  actions: {
    async checkIfNeedTosAcceptance({ state, commit }) {
      const currentUser = this.state.authorization.user;
      /* eslint-disable-next-line vue/max-len */
      const tosEnabled = this.state.featureFlags?.flags?.enable_terms_of_service_acceptance ?? false;
      const tosCheckInProgress = state.currentTosCheckInProgress;
      if (currentUser.id !== undefined && tosEnabled && !tosCheckInProgress) {
        commit('setCurrentTosCheckInProgress', true);
        try {
          const newClient = new TermsOfServiceClient(currentUser);
          const {
            eulaAcceptanceRequired,
            eula,
          } = await newClient.checkNeedTosAcceptance();
          if (eulaAcceptanceRequired) {
            commit('setTosAcceptanceRequired', eulaAcceptanceRequired);
          }
          if (eula?.url !== undefined) {
            commit('setTosContentUrl', eula.url);
          }
          if (eula?.id !== undefined) {
            commit('setEulaId', eula.id);
          }
          const tosLastCheckedDate = dayjs(new Date()).toISOString();
          commit('setLastCheckedTosDate', tosLastCheckedDate);
        } catch (error) {
          commit('setTosErrors', [error]);
        } finally {
          commit('setCurrentTosCheckInProgress', false);
        }
      } else {
        commit('setTosAcceptanceRequired', false);
      }
    },
    async submitTosAcceptance({ state, commit }) {
      try {
        const acceptedDate = dayjs(new Date()).toISOString();
        const payload = {
          eulaId: state.eulaId,
          acceptedDate,
        };
        const currentUser = this.state.authorization.user;
        const newClient = new TermsOfServiceClient(currentUser);
        const response = await newClient.blanketApproveTermsOfService(payload);
        if (response?.eulaAcceptances?.length > 0) {
          commit('setTosAcceptanceRequired', false);
          commit('setTermsOfServiceAccepted', true);
        }
      } catch (error) {
        commit('setTosErrors', [error]);
      }
    },
    clearTosInfo({ commit }) {
      commit('setTosAcceptanceRequired', false);
      commit('setTosContentUrl', '');
      commit('setTermsOfServiceAccepted', false);
      commit('setEulaId', null);
      commit('setTosErrors', []);
      commit('setTosPrintContent', null);
      commit('setTosSuccessToastShown', false);
    },
    setTosErrors({ commit }, error) {
      commit('setTosErrors', [error]);
    },
    setTosPrintContent({ commit }, tosPrintContent) {
      commit('setTosPrintContent', tosPrintContent);
    },
    setCurrentTosCheckInProgress({ commit }, newVal) {
      commit('setCurrentTosCheckInProgress', newVal);
    },
    setTosSuccessToastShown({ commit }, newVal) {
      commit('setTosSuccessToastShown', newVal);
    },
  },
  getters: {
    lastCheckedTosDate(state) {
      return state.lastCheckedTosDate;
    },
    newTosAcceptanceRequired(state) {
      return state.acceptanceRequired;
    },
    newTosContentUrl(state) {
      return state.tosContentUrl;
    },
    getTosErrors(state) {
      return state.errors;
    },
    getEulaId(state) {
      return state.eulaId;
    },
    getTermsOfServiceAccepted(state) {
      return state.termsOfServiceAccepted;
    },
    currentTosCheckInProgress(state) {
      return state.currentTosCheckInProgress;
    },
    getTosSuccessToastShown(state) {
      return state.tosSuccessToastShown;
    },
  },
};

export const {
  mutations, actions, state, getters,
} = termsOfService;

export default termsOfService;
